import React from 'react';
import {
  Search, LayoutDashboard, BarChart2, CheckCircle2, Package, FileBarChart, Sparkles,
} from 'lucide-react';

/**
 * Two blocks:
 *  A — Three problems we solve
 *  B — Platform features (6 items, 2 columns)
 */
export const FeaturesSection = () => (
  <>
    {/* A. Problems */}
    <section className="bg-white py-20 md:py-28" data-testid="problems-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mx-auto mb-14 text-center">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF451A] mb-4">
            Why Vyvre exists
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F0F0F] leading-[1.05]">
            Influencer marketing is broken.
            <br />
            <span className="text-[#FF451A]">We fixed it.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <ProblemCard
            icon={Search}
            problem="Finding the right creator takes weeks"
            solution="Search 19,000+ vetted Indian creators by category, location, language, price and platform in seconds"
          />
          <ProblemCard
            icon={LayoutDashboard}
            problem="Managing 200 creators is complete chaos"
            solution="Every creator tracked from pitch to payment — pipeline, mandates, deliverables, addresses, invoices in one place"
          />
          <ProblemCard
            icon={BarChart2}
            problem="Proving ROI to your client is guesswork"
            solution="Auto-generated campaign reports with CPV, reach, engagement benchmarks — downloadable as PDF or Excel"
          />
        </div>
      </div>
    </section>

    {/* B. Platform features */}
    <section className="bg-[#FAFAFA] border-y border-black/[0.06] py-20 md:py-28" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-[#FF451A] mb-4">
            The Platform
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F0F0F] leading-[1.05]">
            One Platform.
            <br />
            <span className="text-[#FF451A]">Every Step of the Campaign.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          <FeatureCard
            icon={Search}
            title="Creator Discovery"
            body="Search 19,000+ creators by niche, location, language, follower range and price. Verified pricing, real engagement data, exclusivity status."
          />
          <FeatureCard
            icon={LayoutDashboard}
            title="Campaign Management"
            body="Track every creator from pitched to live. Pipeline view, mandate management, bulk WhatsApp outreach — your team stays on top of 200 creators without breaking a sweat."
          />
          <FeatureCard
            icon={CheckCircle2}
            title="Deliverable Approvals"
            body="Creators submit drafts, brands approve or request revisions — directly inside the platform. Full version history, no Google Drive links."
          />
          <FeatureCard
            icon={Package}
            title="Product Seeding"
            body="Collect creator addresses, track dispatch status, export shipping CSV in one click. Built for seeding campaigns with 50 to 500 creators."
          />
          <FeatureCard
            icon={FileBarChart}
            title="Campaign Reports"
            body="When the campaign ends, generate a branded PDF or Excel report with live links, views, reach, CPV and engagement rate — compared against industry benchmarks."
          />
          <FeatureCard
            icon={Sparkles}
            title="Talent Management"
            body="Manage your exclusive creator roster — pricing, availability, exclusivity, media kits, negotiations and earnings — all tracked for every creator."
          />
        </div>
      </div>
    </section>
  </>
);

const ProblemCard = ({ icon: Icon, problem, solution }) => (
  <div
    className="shn-problem-card group relative bg-white border border-black/[0.08] rounded-2xl p-7 transition-all duration-300 hover:bg-[#FAFAFA] hover:border-[#FF451A]/60 hover:shadow-[0_20px_60px_rgba(255,69,26,0.14)] hover:-translate-y-0.5"
  >
    <div className="relative w-14 h-14 flex items-center justify-center mb-5">
      {/* Faint orange circle backdrop */}
      <span
        className="absolute inset-0 rounded-full bg-[#FF451A]/[0.08] group-hover:bg-[#FF451A]/[0.14] transition-colors"
        aria-hidden="true"
      />
      <Icon size={24} className="relative text-[#FF451A]" strokeWidth={1.8} />
    </div>
    <div className="text-xl font-semibold text-[#0F0F0F] mb-2.5 leading-snug tracking-tight">{problem}</div>
    <div className="text-sm text-[#4B5563] leading-relaxed">{solution}</div>
  </div>
);

const FeatureCard = ({ icon: Icon, title, body }) => (
  <div className="bg-white border border-black/[0.08] rounded-2xl p-6 flex gap-4 hover:border-[#FF451A]/40 transition-colors">
    <div className="w-11 h-11 rounded-xl bg-[#FF451A]/10 flex items-center justify-center shrink-0">
      <Icon size={20} className="text-[#FF451A]" />
    </div>
    <div className="flex-1">
      <div className="text-base font-semibold text-[#0F0F0F] mb-1.5">{title}</div>
      <div className="text-sm text-[#4B5563] leading-relaxed">{body}</div>
    </div>
  </div>
);

export default FeaturesSection;
